import {
  BarChart3, Briefcase, Building2, ClipboardList, Handshake, Inbox,
  ClipboardPen, LayoutDashboard, ListChecks, ListTree, Mail, User, UserPlus, Users,
  type LucideIcon,
} from 'lucide-react';
import { strings } from '@/strings';
import type { Role } from '@/types/roles';

/**
 * The icon is part of the entry, not a lookup beside it. The collapsed rail
 * shows icons alone, so an entry without one is a blank square nobody can
 * click with intent — making it a required field turns that into a type error.
 */
/**
 * `label` is the Arabic string; `labelKey` is the key it came from, so the
 * shell can look the same entry up in another language. Both, not one: the
 * label keeps every existing caller working, and the key is what makes the
 * nav translatable at all.
 */
export type NavItem = { to: string; label: string; labelKey: keyof typeof strings; icon: LucideIcon };

/**
 * Each role gets its own tree, not a shared one filtered at render time. A
 * filtered menu leaks its own shape — an empty section still says the section
 * exists — and it makes the wrong link one boolean away from being visible.
 *
 * Adding a page in a later plan means adding a route AND an entry here, which
 * is the point: a page with no nav entry is a page nobody can find.
 */
const NAV: Record<Role, NavItem[]> = {
  admin: [
    { to: '/admin', label: strings.navAdminOverview, labelKey: 'navAdminOverview', icon: LayoutDashboard },
    { to: '/admin/users', label: strings.navAdminUsers, labelKey: 'navAdminUsers', icon: Users },
    { to: '/admin/taxonomy', label: strings.navAdminTaxonomy, labelKey: 'navAdminTaxonomy', icon: ListTree },
    { to: '/admin/invites', label: strings.navStaffInvites, labelKey: 'navStaffInvites', icon: Mail },
    { to: '/admin/orgs', label: strings.orgApprovalsTitle, labelKey: 'orgApprovalsTitle', icon: Building2 },
    { to: '/admin/reports', label: strings.reportsTitle, labelKey: 'reportsTitle', icon: BarChart3 },
  ],
  recruiter: [
    { to: '/recruiter', label: strings.navRecruiterQueue, labelKey: 'navRecruiterQueue', icon: Inbox },
    { to: '/recruiter/candidates', label: strings.navRecruiterCandidates, labelKey: 'navRecruiterCandidates', icon: Users },
    { to: '/recruiter/opportunities', label: strings.navRecruiterOpportunities, labelKey: 'navRecruiterOpportunities', icon: Briefcase },
    { to: '/recruiter/matches', label: strings.navRecruiterMatches, labelKey: 'navRecruiterMatches', icon: Handshake },
    { to: '/recruiter/invites', label: strings.navStaffInvites, labelKey: 'navStaffInvites', icon: Mail },
  ],
  recommender: [
    { to: '/recommender', label: strings.navRecommenderNominate, labelKey: 'navRecommenderNominate', icon: UserPlus },
    { to: '/recommender/mine', label: strings.navRecommenderMine, labelKey: 'navRecommenderMine', icon: ListChecks },
  ],
  'ngo-rep': [
    { to: '/org', label: strings.navOrgAssessment, labelKey: 'navOrgAssessment', icon: ClipboardList },
    { to: '/org/opportunities', label: strings.navOrgOpportunities, labelKey: 'navOrgOpportunities', icon: Briefcase },
    { to: '/org/matches', label: strings.navOrgMatches, labelKey: 'navOrgMatches', icon: Handshake },
  ],
  candidate: [
    { to: '/candidate', label: strings.navCandidateProfile, labelKey: 'navCandidateProfile', icon: User },
    { to: '/candidate/diagnostic', label: strings.navCandidateDiagnostic, labelKey: 'navCandidateDiagnostic', icon: ClipboardPen },
    { to: '/candidate/opportunities', label: strings.navCandidateOpportunities, labelKey: 'navCandidateOpportunities', icon: Briefcase },
  ],
  guest: [{ to: '/welcome', label: strings.navGuestRegisterOrg, labelKey: 'navGuestRegisterOrg', icon: Building2 }],
};

export function navFor(role: Role): NavItem[] {
  return NAV[role];
}

export function homePathFor(role: Role | null): string {
  return role ? NAV[role][0].to : '/';
}
